import Handlebars from 'handlebars'

type Frame = {
  context: unknown
  data: Record<string, unknown>
  parent?: Frame
}

/**
 * Renders a Handlebars template by walking the AST from `Handlebars.parse`.
 *
 * `Handlebars.compile` generates JavaScript source and runs it with
 * `new Function`, which our CSP blocks (`script-src` has no 'unsafe-eval').
 * `Handlebars.parse` does not evaluate code, so it is safe to use.
 *
 * Supported: text, comments, `{{path}}` / `{{{path}}}` (including `this`,
 * `../`, `@index`, `@first`, `@last`, `@root`) and `{{#each}}`, `{{#if}}`,
 * `{{#unless}}` blocks with `{{else}}`. Values are never HTML-escaped, same as
 * `compile` with `noEscape: true`. Any other syntax throws.
 */
export function renderHandlebars(template: string, context: unknown): string {
  const program = Handlebars.parse(template)
  return renderProgram(program, { context, data: { root: context } })
}

function renderProgram(
  program: hbs.AST.Program | undefined,
  frame: Frame
): string {
  if (!program) return ''
  return program.body.map((node) => renderStatement(node, frame)).join('')
}

function renderStatement(node: hbs.AST.Statement, frame: Frame): string {
  switch (node.type) {
    case 'ContentStatement':
      return (node as hbs.AST.ContentStatement).value
    case 'CommentStatement':
      return ''
    case 'MustacheStatement': {
      const mustache = node as hbs.AST.MustacheStatement
      if (mustache.params.length > 0 || mustache.hash) {
        throw new Error(`Unsupported Handlebars helper: ${mustache.loc.source}`)
      }
      const value = resolvePath(mustache.path, frame)
      return value === null || value === undefined ? '' : String(value)
    }
    case 'BlockStatement':
      return renderBlock(node as hbs.AST.BlockStatement, frame)
    default:
      throw new Error(`Unsupported Handlebars syntax: ${node.type}`)
  }
}

function renderBlock(block: hbs.AST.BlockStatement, frame: Frame): string {
  const helper = block.path.original
  if (
    block.params.length !== 1 ||
    block.hash ||
    block.program.blockParams?.length
  ) {
    throw new Error(`Unsupported Handlebars block: {{#${helper}}}`)
  }

  const value = resolvePath(block.params[0], frame)

  switch (helper) {
    case 'if':
      return renderProgram(
        isTruthy(value) ? block.program : block.inverse,
        frame
      )
    case 'unless':
      return renderProgram(
        isTruthy(value) ? block.inverse : block.program,
        frame
      )
    case 'each': {
      if (!Array.isArray(value) || value.length === 0) {
        return renderProgram(block.inverse, frame)
      }
      return value
        .map((item, index) =>
          renderProgram(block.program, {
            context: item,
            data: {
              ...frame.data,
              index,
              first: index === 0,
              last: index === value.length - 1,
            },
            parent: frame,
          })
        )
        .join('')
    }
    default:
      throw new Error(`Unsupported Handlebars block: {{#${helper}}}`)
  }
}

function resolvePath(expression: hbs.AST.Expression, frame: Frame): unknown {
  if (expression.type !== 'PathExpression') {
    throw new Error(`Unsupported Handlebars expression: ${expression.type}`)
  }
  const path = expression as hbs.AST.PathExpression

  let target: Frame | undefined = frame
  for (let i = 0; i < path.depth; i++) target = target?.parent
  if (!target) return undefined

  // Own properties only, matching Handlebars' default prototype access rules
  let value: unknown = path.data ? target.data : target.context
  for (const part of path.parts) {
    if (
      value === null ||
      value === undefined ||
      !Object.prototype.hasOwnProperty.call(value, part)
    ) {
      return undefined
    }
    value = (value as Record<string, unknown>)[part]
  }
  return value
}

// Same rule as the built-in `if` helper: falsy values and empty arrays are false
function isTruthy(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : Boolean(value)
}
